import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

import {
    Box,
    Drawer,
    SwipeableDrawer,
    Hidden,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import CloseIcon from "@mui/icons-material/Close";

import { Outlet, useLocation, useNavigate } from "react-router-dom";

const drawerWidth = 280;

export const MainLayoutContext = createContext({
    toggleDrawer: () => {
        console.warn("Used outside of MainLayout");
    },
});

export const useToggleDrawer = () => {
    const ctx = useContext(MainLayoutContext);
    return ctx.toggleDrawer;
};

export const useLayoutContext = () => {
    return useContext(MainLayoutContext);
};

export const ToggleDrawerButton = () => {
    const { toggleDrawer, drawerOpen } = useContext(MainLayoutContext);
    return <IconButton onClick={toggleDrawer}>{drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}</IconButton>;
};

const MainLayout = ({ menuActions }) => {
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("md"));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const onMenuClick = useCallback(
        (action) => {
            if (action.path) {
                navigate(action.path);
            }
        },
        [navigate]
    );

    useEffect(() => {
        setDrawerOpen(false);
    }, [isSmUp]);

    const toggleDrawer = useCallback(() => {
        setDrawerOpen(!drawerOpen);
    }, [drawerOpen, setDrawerOpen]);

    const contextValue = useMemo(
        () => ({
            toggleDrawer,
            drawerOpen,
            railVisible: isSmUp,
        }),
        [toggleDrawer, drawerOpen, isSmUp]
    );

    const railPaperSx = {
        position: "relative",
        display: "flex",
        direction: "column",
        alignItems: "center",
        overflowX: "hidden",
        overflowY: "auto",
    };
    const drawerPaperSx = { position: "relative" };

    const railBox = {
        mb: 0.5,
        whiteSpace: "nowrap",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
    };

    return (
        <Box
            sx={{
                display: "flex",
                height: "100vh",
                overflow: "hidden",
                backgroundColor: (theme.palette.surfaceContainer || theme.palette.background).main,
            }}>
            <Hidden mdDown>
                <Box sx={{ flex: "0 0 auto" }}>
                    <Drawer
                        open={true}
                        variant="permanent"
                        PaperProps={{
                            style: drawerOpen ? drawerPaperSx : railPaperSx,
                        }}
                        sx={{
                            width: drawerOpen ? drawerWidth : 96,
                            transition: "width 0.15s ease-in-out",
                            overflow: "hidden",
                            backgroundColor: (theme.palette.surfaceContainer || theme.palette.background).main,
                        }}>
                        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflowY: "hidden" }}>
                            <Box
                                sx={(theme) => ({
                                    flex: "0 0 60px",
                                })}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: theme.spacing(2),
                                        right: drawerOpen ? theme.spacing(2) : 28,
                                        transition: "right 0.15s ease-in-out",
                                    }}>
                                    <IconButton onClick={toggleDrawer}>{drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}</IconButton>
                                </Box>
                            </Box>

                            <Box sx={{ flex: "1 1 auto", overflow: "hidden", overflowY: "auto", minHeight: 0 }}>
                                <List>
                                    {Array.isArray(menuActions) &&
                                        menuActions.map((action, idx) => {
                                            let selected =
                                                action.path &&
                                                (action.path === "/" ? location.pathname === action.path : location.pathname.startsWith(action.path || ""));

                                            return (
                                                <Box key={idx} sx={railBox} title={action.title}>
                                                    <ListItem>
                                                        <ListItemButton
                                                            selected={selected}
                                                            onClick={() => onMenuClick(action)}
                                                            sx={{ paddingRight: drawerOpen ? 2 : 1 }}>
                                                            <ListItemIcon>{action.icon}</ListItemIcon>
                                                            {!!drawerOpen && (
                                                                <ListItemText
                                                                    sx={{
                                                                        display: drawerOpen ? "inherit" : "none",
                                                                        opacity: drawerOpen ? 1 : 0,
                                                                        transition: "opacity 0.1s ease-in-out",
                                                                    }}>
                                                                    {action.title}
                                                                </ListItemText>
                                                            )}
                                                        </ListItemButton>
                                                    </ListItem>
                                                    <Box
                                                        sx={{
                                                            height: drawerOpen ? 0 : "inherit",
                                                            marginBottom: drawerOpen ? 0 : 1,
                                                            opacity: drawerOpen ? 0 : 1,
                                                            transition: "height 0.1s ease-in-out",
                                                            overflow: "hidden",
                                                        }}>
                                                        <Typography variant="caption" sx={{ fontWeight: selected ? "bolder" : null }}>
                                                            {action.title}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                </List>
                            </Box>
                        </Box>
                    </Drawer>
                </Box>
            </Hidden>
            <Hidden mdUp>
                <SwipeableDrawer
                    open={!isSmUp && drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    onOpen={() => setDrawerOpen(true)}
                    style={{ zIndex: 1400, minWidth: drawerWidth }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box
                            sx={(theme) => ({
                                flex: "0 0 60px",
                            })}>
                            <Typography color="inherit" sx={{ fontWeight: 500, letterSpacing: 0.5, fontSize: 20 }}>
                                Nadpis Swipable XYZ
                            </Typography>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: theme.spacing(2),
                                    right: drawerOpen ? theme.spacing(2) : 28,
                                    transition: "right 0.15s ease-in-out",
                                }}>
                                <IconButton onClick={() => setDrawerOpen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box
                            sx={(theme) => ({
                                flex: "1 1 auto",
                                overflow: "hidden",
                                overflowY: "auto",
                                minHeight: 0,
                            })}>
                            <List>
                                {Array.isArray(menuActions) &&
                                    menuActions.map((action, idx) => (
                                        <Box key={idx} sx={{ mb: 1, whiteSpace: "nowrap" }} title={action.title}>
                                            <ListItem>
                                                <ListItemButton
                                                    selected={
                                                        action.path &&
                                                        (action.path === "/"
                                                            ? location.pathname === action.path
                                                            : location.pathname.startsWith(action.path || ""))
                                                    }
                                                    onClick={() => {
                                                        onMenuClick(action);
                                                        setDrawerOpen(false);
                                                    }}>
                                                    <ListItemIcon>{action.icon}</ListItemIcon>
                                                    <ListItemText>{action.title}</ListItemText>
                                                </ListItemButton>
                                            </ListItem>
                                        </Box>
                                    ))}
                            </List>
                        </Box>
                    </Box>
                </SwipeableDrawer>
            </Hidden>
            <Box sx={{ flex: "1 1 auto", minHeight: 0, height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <MainLayoutContext.Provider value={contextValue}>
                    <Outlet />
                </MainLayoutContext.Provider>
            </Box>
        </Box>
    );
};

export default MainLayout;
